/**
 * Pop-up "próximo paciente" na tela do MÉDICO.
 *
 * Espelho do [[CallAlertWatcher]] (que avisa a recepção quando o médico chama
 * um paciente), mas no sentido inverso: a recepção, pelo painel lateral da
 * agenda ([[PatientQuickPanel]]), avisa o médico de qual paciente vai entrar
 * no consultório a seguir. Componente global montado no Shell — funciona em
 * qualquer página em que o médico esteja (em consulta, prontuário, etc.).
 *
 * Regras:
 *  - Só aparece para o MÉDICO (role MEDICO). Recepção não vê.
 *  - Só aparece pro médico DO agendamento (`professionalId` = uid dele). O
 *    painel da recepção não envia sem médico; aviso sem o campo não aparece
 *    pra ninguém. Isso também impede outro médico de dar "Ciente" (que apaga
 *    o aviso do RTDB) num paciente que não é dele.
 *  - Avisos são efêmeros (RTDB, ver [[doctorAlert]]) e somem quando o médico
 *    dá "Ciente".
 *  - Ignora avisos antigos (> STALE_MS) pra não pipocar aviso velho ao abrir o
 *    sistema, e limpa os envelhecidos que ficaram sem "ciente" (médico
 *    offline). Toca o mesmo ding da chamada pra reforçar em qualquer tela.
 *  - Vários avisos viram uma fila: "Ciente" passa pro próximo.
 *  - "Atlas, abre o próximo" (03/10/2026) faz o mesmo que clicar no nome;
 *    sem aviso, abre o paciente do cartão PRÓXIMO do painel (ver o efeito).
 *  - A tecla do ditado (Alt+V/pedal, F5 do controle, F13 do teclado
 *    programável) com o aviso na tela (23/10/2026) abre a ficha e a voz do
 *    sistema fala os primeiros nomes.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellRing, X, DoorOpen, Clock, User as UserIcon, ExternalLink, Users, Keyboard } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Role } from '@/types/auth';
import { playCallChime, playVoiceTone } from '@/utils/chime';
import { DoctorAlertService, type DoctorAlertData } from '@/services/firebase/doctorAlert';
import { AppointmentService } from '@/services/firebase/appointments';
import { QueueService } from '@/services/firebase/queue';
import { pickNextToOpen } from '@/utils/nextUp';
import { spokenNameOf } from '@/utils/patientName';
import { useComandoVoz } from '@/services/voice/wake/commands';
import { botaoDoControle, useControleLigado } from '@/services/voice/controle';
import { isVoiceEnabled } from '@/services/voice/flag';
import { falar } from '@/services/voice/fala';
import { DICTATION_HOTKEY_CODE } from '@/components/voice/DictationHotkey';

/** Avisos mais velhos que isso não pipocam e são limpos (ms). */
const STALE_MS = 5 * 60 * 1000;

function fmtTime(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export const NextPatientWatcher = () => {
  const { clinicId, role, user } = useAuth();
  const uid = user?.uid;
  const navigate = useNavigate();
  // Fila local de avisos aguardando "ciência" do médico (um de cada vez).
  const [pending, setPending] = useState<DoctorAlertData[]>([]);
  // ids já vistos nesta sessão — só reage a avisos novos.
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!clinicId || role !== Role.MEDICO || !uid) return;
    seenRef.current = new Set();
    const unsub = DoctorAlertService.subscribe(clinicId, (all) => {
      const now = Date.now();
      // Limpa avisos velhos sem "ciente" (médico estava offline) pra não
      // acumular lixo no RTDB — de qualquer médico, já que velho é velho.
      all
        .filter((a) => a.createdAt > 0 && now - a.createdAt >= STALE_MS)
        .forEach((a) => void DoctorAlertService.dismiss(clinicId, a.id));
      const fresh = all.filter(
        (a) =>
          a.professionalId === uid &&
          !seenRef.current.has(a.id) &&
          now - a.createdAt < STALE_MS,
      );
      if (fresh.length === 0) return;
      fresh.forEach((a) => seenRef.current.add(a.id));
      setPending((q) => {
        const keys = new Set(q.map((p) => p.id));
        const add = fresh.filter((a) => !keys.has(a.id));
        return add.length ? [...q, ...add] : q;
      });
      playCallChime();
    });
    return () => unsub();
  }, [clinicId, role, uid]);

  // "Atlas, abre o próximo" (spec 011, 03/10/2026): a escuta local ou o
  // ao vivo pedem (`commands.ts`), e aqui abre — o aviso pendente (o do
  // pop-up, igual a clicar no nome), senão quem acabou de ser chamado por
  // este médico, senão o MESMO paciente do cartão PRÓXIMO do painel
  // (`pickNextToOpen`). Até 05/10 só valia a estrela marcada na mão: sem
  // estrela, o "próximo" que o painel mostrava não abria. Quem pede por voz
  // pode não estar olhando: o bipe diz se abriu ou se não havia ninguém.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useComandoVoz(
    'proximo',
    () => {
      if (!clinicId || !uid) return false;
      // Devolve se abriu (o brilho do "Atlas" mostra ✓ ou ✕).
      return (async () => {
        const aviso = pendingRef.current.find((a) => a.patientId);
        if (aviso) {
          void DoctorAlertService.dismiss(clinicId, aviso.id);
          setPending((q) => q.filter((p) => p.id !== aviso.id));
          playVoiceTone('pronto');
          navigate(`/pacientes/${aviso.patientId}`);
          return true;
        }
        try {
          const inicio = new Date();
          inicio.setHours(0, 0, 0, 0);
          const [hoje, fila] = await Promise.all([
            AppointmentService.getAppointmentsByDateRange(
              clinicId, inicio.getTime(), inicio.getTime() + 24 * 60 * 60 * 1000 - 1,
            ),
            QueueService.getToday(clinicId),
          ]);
          const proximo = pickNextToOpen(hoje, fila, uid);
          if (proximo) {
            playVoiceTone('pronto');
            navigate(`/pacientes/${proximo.patientId}`);
            return true;
          }
        } catch (err) {
          console.warn('[voz] "abre o próximo": não consegui ler a agenda de hoje:', err);
        }
        console.info('[voz] "abre o próximo": nenhum aviso pendente nem paciente aguardando hoje');
        playVoiceTone('encerrado');
        return false;
      })();
    },
    !!clinicId && role === Role.MEDICO && !!uid,
  );

  // Tecla do ditado com o aviso na tela (pedido do Leopoldo de 23/10/2026):
  // abre a ficha de quem vai entrar e a voz do sistema (`fala.ts`) fala os
  // primeiros nomes — mãos na lâmpada, sem mouse. A tecla é a mesma do
  // `DictationHotkey` (Alt+V/pedal; F13; F5 com o controle ligado neste
  // computador), em qualquer página. Escuta na CAPTURA e para a tecla ali:
  // senão o `DictationHotkey` abriria/fecharia o ditado do paciente que sai.
  // A próxima pisada, já sem aviso, é o ditado de sempre.
  const controle = useControleLigado();
  const voz = isVoiceEnabled(role);
  const temAviso = pending.length > 0;
  useEffect(() => {
    if (!temAviso || !clinicId || !voz) return;
    const onKey = (e: KeyboardEvent) => {
      const altV = e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === DICTATION_HOTKEY_CODE;
      if (!altV && botaoDoControle(e, controle) !== 'ditado') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      // Pedal segurado repete o keydown: só a primeira pisada conta.
      if (e.repeat) return;
      const aviso = pendingRef.current[0];
      if (!aviso) return;
      void falar(spokenNameOf(aviso.patientName));
      // Sem ficha pra abrir (aviso sem paciente), só fala; o "Ciente" fecha.
      if (!aviso.patientId) return;
      void DoctorAlertService.dismiss(clinicId, aviso.id);
      setPending((q) => q.filter((p) => p.id !== aviso.id));
      navigate(`/pacientes/${aviso.patientId}`);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [temAviso, clinicId, voz, controle, navigate]);

  if (pending.length === 0) return null;

  const current = pending[0];
  const remaining = pending.length - 1;
  const ack = () => {
    if (clinicId) void DoctorAlertService.dismiss(clinicId, current.id);
    setPending((q) => q.slice(1));
  };

  // Clicar no nome abre a ficha do paciente — dá ciência no aviso e navega.
  const openFicha = () => {
    if (!current.patientId) return;
    ack();
    navigate(`/pacientes/${current.patientId}`);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 animate-fade-in"
      role="alertdialog"
      aria-modal="true"
      aria-label="Próximo paciente"
    >
      <div className="bg-surface rounded-2xl shadow-2xl border-2 border-primary/40 w-full max-w-md overflow-hidden">
        <div className="px-5 py-3 flex items-center gap-2 bg-primary text-on-primary">
          <BellRing size={22} className="shrink-0" />
          <span className="font-headline font-bold text-lg">Próximo paciente</span>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-on-surface-variant font-bold">
              Vai entrar no consultório
            </p>
            {current.patientId ? (
              <button
                type="button"
                onClick={openFicha}
                title="Abrir a ficha do paciente"
                className="group text-left inline-flex items-start gap-1.5 text-2xl font-bold text-on-surface break-words hover:text-primary transition-colors"
              >
                <span className="group-hover:underline">{current.patientName}</span>
                <ExternalLink size={16} className="shrink-0 mt-1.5 opacity-60 group-hover:opacity-100" />
              </button>
            ) : (
              <p className="text-2xl font-bold text-on-surface break-words">{current.patientName}</p>
            )}
          </div>

          {/* Acompanhante do dia — SEMPRE visível: ou o nome, ou "Sem
              acompanhante". O médico sabe antes de a porta abrir. */}
          <div
            className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${
              current.accompanistName
                ? 'bg-primary/10 border-primary/20'
                : 'bg-surface-container border-outline-variant/20'
            }`}
          >
            <Users
              size={18}
              className={`shrink-0 ${current.accompanistName ? 'text-primary' : 'text-on-surface-variant'}`}
            />
            <div className="min-w-0">
              <p className="text-[10px] uppercase text-on-surface-variant font-bold">
                Acompanhante
              </p>
              {current.accompanistName ? (
                <p className="text-on-surface font-medium truncate">{current.accompanistName}</p>
              ) : (
                <p className="text-on-surface-variant italic">Sem acompanhante</p>
              )}
            </div>
          </div>

          {(current.room || current.sentByName) && (
            <div className="grid grid-cols-2 gap-3">
              {current.room && (
                <div className="flex items-center gap-2">
                  <DoorOpen size={18} className="shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Sala</p>
                    <p className="text-on-surface font-medium truncate">{current.room}</p>
                  </div>
                </div>
              )}
              {current.sentByName && (
                <div className="flex items-center gap-2">
                  <UserIcon size={18} className="shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Avisado por</p>
                    <p className="text-on-surface font-medium truncate">{current.sentByName}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {current.createdAt > 0 && (
            <p className="text-xs text-on-surface-variant flex items-center gap-1">
              <Clock size={12} /> Avisado às {fmtTime(current.createdAt)}
            </p>
          )}

          {voz && current.patientId && (
            <p className="text-xs text-on-surface-variant flex items-center gap-1">
              <Keyboard size={12} />
              <span>
                <kbd className="font-mono font-bold">{controle ? 'F5' : 'Alt+V'}</kbd> abre a ficha e fala o nome
              </span>
            </p>
          )}

          {remaining > 0 && (
            <p className="text-xs font-bold text-primary">
              + {remaining} outro{remaining > 1 ? 's' : ''} aviso{remaining > 1 ? 's' : ''} na fila
            </p>
          )}

          <button
            onClick={ack}
            className="w-full py-3 rounded-xl font-bold text-base bg-primary text-on-primary hover:brightness-110 transition-all flex items-center justify-center gap-2"
          >
            <X size={18} /> Ciente
          </button>
        </div>
      </div>
    </div>
  );
};
